import { useEffect, useState } from 'react';
import { Box, Button, Text, HStack, CloseButton } from '@chakra-ui/react';

/**
 * 
 * UpdateBanner
 *
 * Shows a small fixed banner when a new build is available, so an admin with a
 * long-lived session is told to reload instead of unknowingly running stale code.
 *
 * Wiring: main.jsx registers the service worker with registerType 'prompt'.
 * When a worker is waiting AND the running commit differs from version.json,
 * it sets window.__pwaNeedRefresh = true and dispatches 'pwa:need-refresh'.
 * This component reads the flag on mount (so it can't miss an event that
 * fired before it mounted) and listens for the event. Reload calls
 * window.__applyUpdate(), which activates the waiting SW and reloads.
 *
 */
export const UpdateBanner = () => {
  const [show, setShow] = useState(() => window.__pwaNeedRefresh === true);

  useEffect(() => {
    const handler = () => setShow(true);
    // Catch an event that fired between the initial render and this effect.
    if (window.__pwaNeedRefresh) setShow(true);
    window.addEventListener('pwa:need-refresh', handler);
    return () => window.removeEventListener('pwa:need-refresh', handler);
  }, []);

  if (!show) return null;

  const reload = () => {
    if (typeof window.__applyUpdate === 'function') {
      window.__applyUpdate();   // activates waiting SW and reloads
    } else {
      window.location.reload(); // fallback if the hook wasn't wired
    }
  };

  return (
    <Box
      position="fixed"
      bottom="16px"
      left="50%"
      transform="translateX(-50%)"
      bg="gray.800"
      color="white"
      px="4"
      py="3"
      borderRadius="md"
      boxShadow="lg"
      zIndex={2000}
      maxW="90vw"
    >
      <HStack spacing="4">
        <Text fontSize="sm">New version available.</Text>
        <Button size="sm" colorScheme="blue" onClick={reload}>
          Reload
        </Button>
        <CloseButton size="sm" onClick={() => setShow(false)} />
      </HStack>
    </Box>
  );
};